import { useEffect, useRef, useState } from "react";
import type { ApiError } from "../../../api/client";
import { FailureAlert } from "../../../components/common/FailureAlert";
import { useTranslation } from "react-i18next";
import {
  CAlert,
  CButton,
  CSpinner,
  CFormCheck,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CRating,
  CStepper,
} from "@coreui/react-pro";
import {
  EXPLANATION_MAX_LENGTH,
  suggestedDisplayName,
  isRateLimited,
  RATE_WINDOW_MS,
  useRateStrategy,
  withMinimumDuration,
} from "../../../api/hooks/useTradingStrategyFeedback";
import { useCustomerProfile } from "../../../api/hooks/useCustomerProfile";
import type { TradingStrategyDto } from "../../../api/hooks/useTradingStrategies";

/** Long enough to read the confirmation, short enough not to feel stuck. */
const CONFIRMATION_MS = 5000;

// CStepper's exposed imperative API. Same shape the End and Resume wizards use, so the three flows
// stay aligned.
interface CStepperRef {
  next: () => void;
  prev: () => void;
  finish: () => void;
  reset: () => void;
}

interface RateStrategyModalProps {
  strategy: TradingStrategyDto;
  visible: boolean;
  onClose: () => void;
  /** Fired the moment the send succeeds, not when the confirmation finishes. */
  onSent: () => void;
  /** Fired when the server refuses on rate limit, with when it will accept again. */
  onRateLimited: (until: Date) => void;
  onRated: () => void;
}

export function RateStrategyModal({
  strategy,
  visible,
  onClose,
  onSent,
  onRateLimited,
  onRated,
}: RateStrategyModalProps) {
  const { t } = useTranslation("tradingStrategies");
  const profile = useCustomerProfile();
  const stepperRef = useRef<CStepperRef>(null);
  const [stars, setStars] = useState(0);
  const [hoveredStars, setHoveredStars] = useState<number | null>(null);
  const [explanation, setExplanation] = useState("");
  const [consented, setConsented] = useState(false);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [failed, setFailed] = useState<{ reference?: string } | null>(null);
  const [slowDown, setSlowDown] = useState(false);
  const [sent, setSent] = useState(false);
  // Spans the padded send, unlike the mutation's own pending flag which clears as soon as the request
  // returns - which is exactly the moment that is too early to stop showing the spinner.
  const [submitting, setSubmitting] = useState(false);
  const rate = useRateStrategy();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Confirmed in place rather than by the modal vanishing - closing the moment it succeeds looks
  // indistinguishable from the form giving up. It closes itself once the message has been read.
  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  // Editable, but only seeded once the profile arrives — and whatever they type is what travels.
  const suggested = suggestedDisplayName(
    profile.data?.firstName,
    profile.data?.lastName,
  );
  const attribution = displayName ?? suggested;

  // Stars are the rating; the words are optional. Attribution is still required, because it is how a
  // quote would be signed if they ever consent to one.
  const incomplete = stars < 1 || !attribution.trim();

  // Hovering previews the judgement, so the word tracks the pointer and falls back to the choice.
  const shownStars = hoveredStars ?? stars;

  // Named so the stars mean something. A number out of five is a value; a word is a judgement, and
  // the judgement is what we are asking for.
  const starLabel = (value: number) => t(`feedback.rating.stars.${value}`);
  const starLabels = [1, 2, 3, 4, 5].map(starLabel);

  const handleSubmit = async () => {
    setFailed(null);
    setSlowDown(false);
    setSubmitting(true);
    try {
      await withMinimumDuration(
        rate.mutateAsync({
          strategyId: strategy.id,
          stars,
          explanation: explanation.trim(),
          consentedToPublication: consented,
          displayName: attribution.trim(),
        }),
      );
      // Counted here rather than in the confirmation callback: closing the dialog early
      // with Cancel or the cross must not lose the fact that it was sent.
      onSent();
      setSent(true);
      closeTimer.current = setTimeout(onRated, CONFIRMATION_MS);
    } catch (error) {
      if (isRateLimited(error)) {
        setSlowDown(true);
        onRateLimited(new Date(Date.now() + RATE_WINDOW_MS));
      } else {
        setFailed({ reference: (error as ApiError)?.traceId });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    {
      label: t("feedback.rating.stepRating"),
      content: (
        <div className="pt-3">
          {/* The verdict leads. Stars at a size worth aiming at, and the word beside them doing the
              work a bare number cannot - a person picking stars is forming a judgement. */}
          <div className="text-center py-3">
            <CRating
              className="justify-content-center"
              size="lg"
              value={stars}
              tooltips={starLabels}
              onChange={(value: number | null) => setStars(Number(value ?? 0))}
              onHover={(value: number | null) => setHoveredStars(value)}
            />
            {/* Both lines are always rendered at a reserved height. The modal is centred, so any
                change in its height re-centres it and slides the stars out from under the pointer -
                which fires another hover, resizes again, and flickers. Fixed heights stop the loop. */}
            <div className="mt-3 fs-4 fw-semibold" style={{ minHeight: "2.25rem" }}>
              {shownStars > 0 ? (
                starLabel(shownStars)
              ) : (
                <span className="text-body-secondary fs-6 fw-normal">
                  {t("feedback.rating.pickAStar")}
                </span>
              )}
            </div>
            <div className="text-body-secondary small" style={{ minHeight: "1.25rem" }}>
              {shownStars > 0 ? t("feedback.rating.outOfFive", { stars: shownStars }) : " "}
            </div>
          </div>

          <hr />
          <div className="d-flex justify-content-between">
            <CButton color="secondary" variant="outline" onClick={onClose}>
              {t("common:buttons.cancel")}
            </CButton>
            <CButton
              color="primary"
              disabled={stars < 1}
              onClick={() => stepperRef.current?.next()}
            >
              {t("common:buttons.next")}
            </CButton>
          </div>
        </div>
      ),
    },
    {
      label: t("feedback.rating.stepReason"),
      content: (
        <div className="pt-3">
          <CFormLabel htmlFor="rating-explanation">
            {t("feedback.rating.why", {
              verdict: starLabel(Math.max(stars, 1)).toLowerCase(),
            })}{" "}
            <span className="text-body-secondary fw-normal">
              {t("feedback.rating.optional")}
            </span>
          </CFormLabel>
          <CFormTextarea
            id="rating-explanation"
            rows={6}
            value={explanation}
            maxLength={EXPLANATION_MAX_LENGTH}
            placeholder={t("feedback.rating.placeholder")}
            onChange={(e) => setExplanation(e.target.value)}
          />
          <div className="d-flex justify-content-between gap-3 form-text">
            <span>{t("feedback.rating.encouragement")}</span>
            <span style={{ whiteSpace: "nowrap" }}>
              {explanation.length} / {EXPLANATION_MAX_LENGTH}
            </span>
          </div>

          {/* Consent and attribution stay together: separated, "Shown as" read as though the name
              would be shown regardless, which is the opposite of what the tick means. */}
          <div className="mt-4">
            <CFormCheck
              id="rating-consent"
              label={t("feedback.rating.consent")}
              checked={consented}
              onChange={(e) => setConsented(e.target.checked)}
            />
            <div className="ps-4 mt-3">
              <CFormLabel htmlFor="rating-display-name" className="small mb-1">
                {t("feedback.rating.shownAs")}
              </CFormLabel>
              <CFormInput
                id="rating-display-name"
                value={attribution}
                maxLength={100}
                disabled={!consented}
                onChange={(e) => setDisplayName(e.target.value)}
              />
              <div className="form-text">
                {consented
                  ? t("feedback.rating.shownAsConsented")
                  : t("feedback.rating.shownAsWithheld")}
              </div>
            </div>
          </div>

          <p className="text-body-secondary small mt-3 mb-0">
            {t("feedback.rating.note")}
          </p>

          {failed && (
            <FailureAlert
              message={t("feedback.rating.failed")}
              reference={failed.reference}
              className="small mt-3 mb-0 py-2"
            />
          )}

          {slowDown && (
            <CAlert color="warning" className="small mt-3 mb-0 py-2">
              {t("feedback.rating.slowDown")}
            </CAlert>
          )}

          <hr />
          <div className="d-flex justify-content-between">
            <CButton
              color="secondary"
              variant="outline"
              onClick={() => stepperRef.current?.prev()}
              disabled={submitting}
            >
              {t("common:buttons.back")}
            </CButton>
            <CButton color="primary" disabled={incomplete || submitting} onClick={handleSubmit}>
              {submitting ? (
                <>
                  <CSpinner size="sm" className="me-2" aria-hidden="true" />
                  {t("feedback.rating.submitting")}
                </>
              ) : (
                t("feedback.rating.submit")
              )}
            </CButton>
          </div>
        </div>
      ),
    },
  ];

  return (
    <CModal visible={visible} onClose={onClose} alignment="center" size="lg">
      <CModalHeader>
        <CModalTitle>{t("feedback.rating.title", { name: strategy.publicName })}</CModalTitle>
      </CModalHeader>
      <CModalBody>
        {sent ? (
          <CAlert color="success" className="mb-0">
            <div className="fw-semibold">{t("feedback.rating.sentTitle")}</div>
            <div className="small">
              {consented
                ? t("feedback.rating.sentConsented")
                : t("feedback.rating.sentWithheld")}
            </div>
          </CAlert>
        ) : (
          // Stepped like the End and Resume wizards: one decision at a time, and the reason is asked
          // only once a rating exists, so it reads as justifying a choice rather than as a hurdle.
          <CStepper ref={stepperRef} steps={steps} />
        )}
      </CModalBody>
    </CModal>
  );
}
